import { oc } from "@orpc/contract";
import { z } from "zod";
import { ediGuidelineContracts } from "./edi/guidelines.contract";

// ---------------------------------------------------------------------------
// Shared schemas
// ---------------------------------------------------------------------------

const PageInfoSchema = z.object({
  currentPage: z.number().int(),
  totalPages: z.number().int(),
  totalCount: z.number().int(),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
});

const Resend855InputSchema = z.object({
  salesOrderId: z.number().int().positive(),
});

const Resend855OutputSchema = z.object({
  /** true = 855 was sent, false = 855 was skipped (disabled for customer) */
  sent: z.boolean(),
});

// ---------------------------------------------------------------------------
// Famous Sales Orders (EDI Invoicing)
// ---------------------------------------------------------------------------

const OrderFetchStatusSchema = z.enum([
  "pending",
  "fetching",
  "success",
  "failed",
]);

const EdiInvoiceTypeSchema = z.enum(["810", "880"]);

const FamousSalesOrderSchema = z.object({
  kind: z.literal("famous_sales_order"),
  id: z.string().uuid(),
  famousSalesOrderId: z.string(),
  orderDetails: z.unknown().nullable(),
  orderFetchStatus: OrderFetchStatusSchema,
  orderFetchError: z.record(z.string(), z.unknown()).nullable(),
  edi810SentAt: z.coerce.date().nullable(),
  edi810Status: z.string().nullable(),
  edi810StatusLastFetchedAt: z.coerce.date().nullable(),
  ediInvoiceSentAt: z.coerce.date().nullable(),
  ediInvoiceStatus: z.string().nullable(),
  ediInvoiceTransactionId: z.string().nullable(),
  ediInvoiceValidationStatus: z.string().nullable(),
  ediInvoiceDeliveryStatus: z.string().nullable(),
  ediInvoiceStatusLastFetchedAt: z.coerce.date().nullable(),
  ediInvoiceType: EdiInvoiceTypeSchema.nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  salesOrderId: z.string().uuid().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type FamousSalesOrderDTO = z.infer<typeof FamousSalesOrderSchema>;

export const EdiSendStatusSchema = z.enum(["sent", "error", "not_sent"]);
export type EdiSendStatus = z.infer<typeof EdiSendStatusSchema>;

const ListFamousSalesOrdersInputSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  orderFetchStatus: OrderFetchStatusSchema.optional(),
  famousSalesOrderId: z.string().optional(),
  purchaseOrderNumber: z.string().optional(),
  id: z.string().uuid().optional(),
  customerId: z.string().uuid().optional(),
  ediInvoiceType: EdiInvoiceTypeSchema.optional(),
  ediSendStatus: EdiSendStatusSchema.optional(),
  importedFrom: z.coerce.date().optional(),
  importedTo: z.coerce.date().optional(),
});

export type ListFamousSalesOrdersInput = z.infer<
  typeof ListFamousSalesOrdersInputSchema
>;

const ListFamousSalesOrdersOutputSchema = z.object({
  edges: z.array(FamousSalesOrderSchema),
  pageInfo: PageInfoSchema,
});

export type ListFamousSalesOrdersOutput = z.infer<
  typeof ListFamousSalesOrdersOutputSchema
>;

const RefreshFamousSalesOrderInputSchema = z.object({
  famousSalesOrderId: z.string().uuid(),
  // Admin-only. When true, bypasses the "already sent" lock and clears the
  // sent-tracking fields so the invoice can be re-sent after refresh.
  force: z.boolean().optional(),
});

const RefreshFamousSalesOrderOutputSchema = FamousSalesOrderSchema;

export type RefreshFamousSalesOrderInput = z.infer<
  typeof RefreshFamousSalesOrderInputSchema
>;

export type RefreshFamousSalesOrderOutput = z.infer<
  typeof RefreshFamousSalesOrderOutputSchema
>;

// ---------------------------------------------------------------------------
// Orderful Config CRUD
// ---------------------------------------------------------------------------

const EdiConfigKeySchema = z.enum(["850", "855", "810", "856", "880", "875"]);

export type EdiConfigKey = z.infer<typeof EdiConfigKeySchema>;

export const UpsertOrderfulConfigInputSchema = z.object({
  customerId: z.string().uuid(),
  key: EdiConfigKeySchema,
  value: z.record(z.string(), z.unknown()),
});

export type UpsertOrderfulConfigInput = z.infer<
  typeof UpsertOrderfulConfigInputSchema
>;

const UpsertOrderfulConfigOutputSchema = z.object({
  id: z.string().uuid(),
  key: EdiConfigKeySchema,
  customerId: z.string().uuid(),
  value: z.record(z.string(), z.unknown()),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type UpsertOrderfulConfigOutput = z.infer<
  typeof UpsertOrderfulConfigOutputSchema
>;

export const DeleteOrderfulConfigInputSchema = z.object({
  customerId: z.string().uuid(),
  key: EdiConfigKeySchema,
});

export type DeleteOrderfulConfigInput = z.infer<
  typeof DeleteOrderfulConfigInputSchema
>;

const DeleteOrderfulConfigOutputSchema = z.object({
  success: z.boolean(),
});

export type DeleteOrderfulConfigOutput = z.infer<
  typeof DeleteOrderfulConfigOutputSchema
>;

// ---------------------------------------------------------------------------
// Partner presets — bulk-apply known-good config bundles for a trading partner
// ---------------------------------------------------------------------------

export const PARTNER_PRESET_IDS = ["procurant", "agne"] as const;
export const PartnerPresetIdSchema = z.enum(PARTNER_PRESET_IDS);
export type PartnerPresetId = z.infer<typeof PartnerPresetIdSchema>;

export const ApplyPartnerPresetInputSchema = z.object({
  customerId: z.string().uuid(),
  presetId: PartnerPresetIdSchema,
});

export type ApplyPartnerPresetInput = z.infer<
  typeof ApplyPartnerPresetInputSchema
>;

const ApplyPartnerPresetOutputSchema = z.object({
  /** EDI keys that had their config upserted */
  applied: z.array(EdiConfigKeySchema),
});

export type ApplyPartnerPresetOutput = z.infer<
  typeof ApplyPartnerPresetOutputSchema
>;

// ---------------------------------------------------------------------------
// Partner profiles — the declarative document rules an EDI config can select
// ---------------------------------------------------------------------------

const PartnerProfileSummarySchema = z.object({
  /** The id stored in `OrderfulConfig.value.partnerProfile`. */
  id: z.string(),
  /** Human-readable name, e.g. "Pattison Food Group (X12 V5010)". */
  label: z.string(),
  /**
   * The EDI document codes this profile can build, e.g. `["855", "810", "856"]`.
   * Lets the settings UI offer a profile only on the sections it actually
   * covers, instead of listing one that would throw at send time.
   */
  documents: z.array(z.string()),
});

export type PartnerProfileSummaryDTO = z.infer<
  typeof PartnerProfileSummarySchema
>;

const ListPartnerProfilesOutputSchema = z.object({
  profiles: z.array(PartnerProfileSummarySchema),
});

export type ListPartnerProfilesOutput = z.infer<
  typeof ListPartnerProfilesOutputSchema
>;

// ---------------------------------------------------------------------------
// Manual order import — replay an Orderful 850/875 through the poller pipeline
// ---------------------------------------------------------------------------

const ImportableOrderTypeSchema = z.enum([
  "850_PURCHASE_ORDER",
  "875_GROCERY_PRODUCTS_PURCHASE_ORDER",
]);

export const ImportOrderfulTransactionInputSchema = z.object({
  // Orderful transaction IDs are numeric and get interpolated into the GET URL;
  // reject anything non-numeric (incl. path-altering chars) before it's used.
  transactionId: z
    .string()
    .trim()
    .regex(/^\d+$/, "Transaction ID must be numeric"),
  /**
   * Re-import from scratch: instead of refusing a transaction that was already
   * imported, discard the draft it produced and ingest it again as if for the
   * first time. For trialling the same order repeatedly.
   */
  test: z.boolean().optional(),
});

export type ImportOrderfulTransactionInput = z.infer<
  typeof ImportOrderfulTransactionInputSchema
>;

const ImportOrderfulTransactionOutputSchema = z.object({
  orderfulMessageId: z.string().uuid(),
  stagedPurchaseOrderId: z.string().uuid(),
  purchaseOrderNumber: z.string(),
  transactionType: ImportableOrderTypeSchema,
  stream: z.string(),
  customerName: z.string().nullable(),
  /**
   * How many existing drafts a `test` re-import discarded (0 on a first
   * import). Surfaced so the caller can say what was replaced rather than
   * silently swapping the order out from under the user.
   */
  replacedDraftCount: z.number().int().nonnegative(),
  /**
   * Set when a discarded draft had already been approved into a SalesOrder —
   * that order and anything it created in the ERP still exist. Not an error,
   * but the user needs to know the re-import didn't undo it.
   */
  warning: z.string().nullable(),
});

export type ImportOrderfulTransactionOutput = z.infer<
  typeof ImportOrderfulTransactionOutputSchema
>;

// ---------------------------------------------------------------------------
// Famous Order Confirmations (EDI 855)
// ---------------------------------------------------------------------------

const FamousOrderConfirmationSchema = z.object({
  id: z.string().uuid(),
  famousOrderId: z.string(),
  orderDetails: z.unknown().nullable(),
  orderFetchStatus: OrderFetchStatusSchema,
  orderFetchError: z.record(z.string(), z.unknown()).nullable(),
  edi855SentAt: z.coerce.date().nullable(),
  edi855Status: z.string().nullable(),
  edi855TransactionId: z.string().nullable(),
  edi855Error: z.string().nullable(),
  edi855StatusLastFetchedAt: z.coerce.date().nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type FamousOrderConfirmationDTO = z.infer<
  typeof FamousOrderConfirmationSchema
>;

/**
 * Where a listed 855 came from.
 *
 * `order_confirmation` — a `FamousOrderConfirmation` record: a Famous order
 * imported onto this page, which may or may not have been acknowledged yet.
 * These rows carry the page's actions (sync, send, delete).
 *
 * `orderful_message` — an outbound 855 that no confirmation record represents,
 * read straight from the Orderful message log. In practice these are the
 * acknowledgments fired automatically on SalesOrder approval
 * (`triggerSource: "sales_order_creation"`), which never create a confirmation
 * record. They are read-only apart from Re-send: without them the page would
 * under-report, which is exactly why the separate "855 Acknowledgments" page
 * existed.
 */
const Outbound855RowSourceSchema = z.enum([
  "order_confirmation",
  "orderful_message",
]);

export type Outbound855RowSource = z.infer<typeof Outbound855RowSourceSchema>;

const Outbound855RowSchema = FamousOrderConfirmationSchema.extend({
  source: Outbound855RowSourceSchema,
  /**
   * Null on `orderful_message` rows: there was no Famous fetch to report on.
   * Never null on `order_confirmation` rows.
   */
  orderFetchStatus: OrderFetchStatusSchema.nullable(),
  /** BAK03 for message rows; the cached Famous order's PO for confirmations. */
  purchaseOrderNumber: z.string().nullable(),
  /** Who the acknowledgment was addressed to (ISA receiver). */
  receiverIsaId: z.string().nullable(),
  /** SalesOrder.internalId from REF*VN — what `outbound855Messages.resend` needs. */
  salesOrderId: z.number().int().nullable(),
});

export type Outbound855RowDTO = z.infer<typeof Outbound855RowSchema>;

const ListOrderConfirmationsInputSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  famousOrderId: z.string().optional(),
  customerId: z.string().uuid().optional(),
  edi855Status: z.enum(["not_sent", "pending", "sent", "error"]).optional(),
});

const ListOrderConfirmationsOutputSchema = z.object({
  edges: z.array(Outbound855RowSchema),
  pageInfo: PageInfoSchema,
});

export type ListOrderConfirmationsOutput = z.infer<
  typeof ListOrderConfirmationsOutputSchema
>;

const FetchOrderConfirmationInputSchema = z.object({
  famousOrderId: z.string(),
});

const FetchOrderConfirmationOutputSchema = FamousOrderConfirmationSchema;

const GetOrderConfirmationInputSchema = z.object({
  orderConfirmationId: z.string().uuid(),
});

const GetOrderConfirmationOutputSchema = FamousOrderConfirmationSchema;

const SyncOrderConfirmationInputSchema = z.object({
  orderConfirmationId: z.string().uuid(),
  // Admin-only. When true, bypasses the "already sent" lock and clears the
  // 855 sent-tracking fields so the confirmation can be re-sent after refresh.
  force: z.boolean().optional(),
});

const SyncOrderConfirmationOutputSchema = FamousOrderConfirmationSchema;

const DeleteOrderConfirmationInputSchema = z.object({
  orderConfirmationId: z.string().uuid(),
});

const DeleteOrderConfirmationOutputSchema = z.object({
  success: z.boolean(),
});

const SendOrderConfirmation855InputSchema = z.object({
  orderConfirmationId: z.string().uuid(),
  force: z.boolean().optional(),
  /**
   * Send on Orderful's TEST stream and leave the order-confirmation row
   * untouched. Mirrors the 856 shipment route's `test` flag, and matters for the
   * same two reasons: outbound builders otherwise take the *environment's*
   * `orderful.stream`, so on a LIVE deployment a trial send would reach the
   * trading partner; and a trial must not overwrite the real 855 status of an
   * order that has already been acknowledged. Admin-only.
   */
  test: z.boolean().optional(),
});

const SendOrderConfirmation855OutputSchema = z.object({
  sent: z.boolean(),
  /** Echoed so the caller knows which stream the result refers to. */
  test: z.boolean().optional(),
  /**
   * The Orderful transaction id. Always returned for a `test` send, which is
   * the only place it appears — nothing is persisted for one, so without this
   * the send would be unverifiable.
   */
  transactionId: z.string().nullable().optional(),
});

// ---------------------------------------------------------------------------
// 855 from an approved sales order (the other source on the same page)
// ---------------------------------------------------------------------------

/**
 * Look up the approved sales order behind a customer PO, so the operator sees
 * *what* they are about to acknowledge before anything goes on the wire.
 *
 * Separate from the send because a customer PO number is not unique — the same
 * number can appear for more than one order — and picking one silently is how
 * the wrong partner gets acknowledged.
 */
const ResolvePurchaseOrderFor855InputSchema = z.object({
  purchaseOrderNumber: z.string().trim().min(1),
});

const PurchaseOrderAck855MatchSchema = z.object({
  salesOrderId: z.string().uuid(),
  /** The human-facing Gently order number. */
  internalId: z.number().int(),
  purchaseOrderNumber: z.string().nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  status: z.string().nullable(),
  orderDate: z.string().nullable(),
  /**
   * Whether an 855 built from this sales order can be sent, and if not, the
   * reason in terms the operator can act on (usually a config field).
   * Reported per match rather than as a thrown error so the picker can show a
   * blocked order and say why, instead of appearing to find nothing.
   */
  eligible: z.boolean(),
  blockedReason: z.string().nullable(),
});

export type PurchaseOrderAck855Match = z.infer<
  typeof PurchaseOrderAck855MatchSchema
>;

const ResolvePurchaseOrderFor855OutputSchema = z.object({
  matches: z.array(PurchaseOrderAck855MatchSchema),
});

const SendAck855FromSalesOrderInputSchema = z.object({
  /**
   * The sales order the operator was shown by `resolvePurchaseOrder` — not the
   * PO string they typed. Sending by id means the document answers exactly the
   * order that was on screen, even if a second order for that PO appears in
   * between.
   */
  salesOrderId: z.string().uuid(),
  /** Send on Orderful's TEST stream instead of the resolved one. Admin-only. */
  test: z.boolean().optional(),
});

const SendAck855FromSalesOrderOutputSchema = z.object({
  sent: z.boolean(),
  /** Present when `sent` is false: why the customer's config refused it. */
  skippedReason: z.string().nullable().optional(),
  transactionId: z.string().nullable().optional(),
  stream: z.string().nullable().optional(),
  test: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Organization-level EDI settings
// ---------------------------------------------------------------------------

/**
 * Partial update of the organization's own EDI identity — the sender ISA ID
 * (ISA06, max 15 chars per X12) and our DUNS / DUNS+4, which names us as
 * Ship-From (`N1*SF` N104) on outbound documents. Omitting a field leaves it
 * unchanged; passing `null` clears it. Empty strings are normalized to null.
 */
export const UpdateOrganizationEdiIdentityInputSchema = z.object({
  isaId: z.string().trim().max(15).nullable().optional(),
  duns: z.string().trim().max(13).nullable().optional(),
});

export type UpdateOrganizationEdiIdentityInput = z.infer<
  typeof UpdateOrganizationEdiIdentityInputSchema
>;

const OrganizationEdiIdentityOutputSchema = z.object({
  isaId: z.string().nullable(),
  duns: z.string().nullable(),
});

/**
 * Org-wide fallback values used when a customer has no value of its own —
 * distinct from `OrganizationEdiIdentityOutputSchema` above, which is true
 * org identity (ISA ID / DUNS). These are behavioral defaults a customer can
 * override, not facts about us.
 */
const OrganizationEdiDefaultsOutputSchema = z.object({
  /** Org-wide default for `CustomerEdiIdentityOutputSchema.ignoreLineDescription`. */
  ignoreLineDescription: z.string().nullable(),
});

/**
 * Partial update of the org-wide defaults. Omitting a field leaves it
 * unchanged; passing `null` clears it. Empty strings are normalized to null.
 */
export const UpdateOrganizationEdiDefaultsInputSchema = z.object({
  ignoreLineDescription: z.string().trim().max(255).nullable().optional(),
});

export type UpdateOrganizationEdiDefaultsInput = z.infer<
  typeof UpdateOrganizationEdiDefaultsInputSchema
>;

// ---------------------------------------------------------------------------
// Customer-level EDI identity (ISA ID + vendor ID)
// ---------------------------------------------------------------------------

const CustomerEdiIdentityOutputSchema = z.object({
  isaId: z.string().nullable(),
  vendorId: z.string().nullable(),
  /**
   * The customer's DUNS. Their identity on the wire, and the only thing that
   * names them when they trade through a portal whose ISA ID fronts several
   * customers (Procurant).
   */
  duns: z.string().nullable(),
  /**
   * Drop a line entirely — never invoiced, shipped, or acknowledged — when
   * its Famous translate data's "FIS/EDI Description"
   * (`FC_TRANSLATE_PRODUCT.DESCR`) matches, case/whitespace-insensitive.
   * Applies across every outbound document (810/856/855); it's a fact about
   * the item, not about which document type is building.
   *
   * This is the customer's **own** stored value only — `null` here can mean
   * "the org default applies instead" (see `organization.getDefaults`), not
   * necessarily "nothing is excluded." The value a build actually uses is
   * resolved server-side, not shown by this field.
   */
  ignoreLineDescription: z.string().nullable(),
});

export const GetCustomerEdiIdentityInputSchema = z.object({
  customerId: z.string().uuid(),
});

/**
 * Partial update of a customer's EDI identity. Omitting a field leaves it
 * unchanged; passing `null` clears it. Empty strings are normalized to null.
 */
export const UpdateCustomerEdiIdentityInputSchema = z.object({
  customerId: z.string().uuid(),
  isaId: z.string().trim().max(15).nullable().optional(),
  vendorId: z.string().trim().max(80).nullable().optional(),
  duns: z.string().trim().max(13).nullable().optional(),
  ignoreLineDescription: z.string().trim().max(255).nullable().optional(),
});

export type UpdateCustomerEdiIdentityInput = z.infer<
  typeof UpdateCustomerEdiIdentityInputSchema
>;

// ---------------------------------------------------------------------------
// EDI channels — what an ISA ID identifies
//
// Most partners trade with us directly, so their ISA ID *is* them. A portal
// (Procurant) fronts several of our customers, who all trade under its ISA and
// are told apart by the DUNS inside the document. Which one an ISA is, is
// declared here rather than inferred from how many customers happen to be
// configured behind it — see attributeTransactionCustomer.ts.
// ---------------------------------------------------------------------------

const EdiChannelKindSchema = z.enum(["direct", "aggregator"]);

export type EdiChannelKindDTO = z.infer<typeof EdiChannelKindSchema>;

const EdiChannelSchema = z.object({
  isaId: z.string(),
  kind: EdiChannelKindSchema,
  /** Customers configured on this ISA — several is normal for a portal. */
  customerNames: z.array(z.string()),
});

export type EdiChannelDTO = z.infer<typeof EdiChannelSchema>;

const ListEdiChannelsOutputSchema = z.object({
  channels: z.array(EdiChannelSchema),
});

export const SetEdiChannelKindInputSchema = z.object({
  isaId: z.string().trim().min(1).max(15),
  kind: EdiChannelKindSchema,
});

export type SetEdiChannelKindInput = z.infer<
  typeof SetEdiChannelKindInputSchema
>;

// ---------------------------------------------------------------------------
// Orderful Transactions (admin) — live list of this environment's transactions,
// attributed to the customer they belong to.
//
// Attribution is not the same question as filtering. Orderful can narrow by
// partner ISA ID, but an aggregator (Procurant) puts many customers behind ONE
// ISA ID, so the ISA names the channel, not the customer. The end customer is
// resolved from the DUNS on the document's party loops — which is why a row can
// say "Safeway" on a transaction whose ISA is `PROCURANT`.
// ---------------------------------------------------------------------------

const OrderfulTransactionCustomerSchema = z.object({
  customerId: z.string().uuid(),
  customerName: z.string(),
  /** Vendor number, when the customer has one. Not every EDI partner does. */
  vendorId: z.string().nullable(),
  /** Receiver ISA ID. Shared between customers behind an aggregator. */
  isaId: z.string().nullable(),
});

export type OrderfulTransactionCustomerDTO = z.infer<
  typeof OrderfulTransactionCustomerSchema
>;

const ListOrderfulTransactionCustomersOutputSchema = z.object({
  customers: z.array(OrderfulTransactionCustomerSchema),
});

const OrderfulTransactionSchema = z.object({
  /** Orderful transaction id. */
  id: z.string(),
  /** Orderful message type name, e.g. "850_PURCHASE_ORDER". */
  transactionType: z.string().nullable(),
  /** 3-digit EDI document code, e.g. "850". */
  documentCode: z.string().nullable(),
  stream: z.string().nullable(),
  senderIsaId: z.string().nullable(),
  receiverIsaId: z.string().nullable(),
  /** The end customer this transaction was attributed to, when resolvable. */
  matchedCustomerName: z.string().nullable(),
  matchedCustomerId: z.string().uuid().nullable(),
  validationStatus: z.string().nullable(),
  deliveryStatus: z.string().nullable(),
  acknowledgmentStatus: z.string().nullable(),
  /** Deep link to the raw transaction view in the Orderful dashboard. */
  orderfulUrl: z.string().nullable(),
  /** Readable rendering of the document — what "the link" means to a user. */
  orderfulPrintViewUrl: z.string().nullable(),
  createdAt: z.coerce.date().nullable(),
});

export type OrderfulTransactionDTO = z.infer<typeof OrderfulTransactionSchema>;

const ListOrderfulTransactionsInputSchema = z.object({
  /**
   * Narrow to one configured customer. Omit to list every customer's traffic.
   * A customer behind a shared ISA ID (Procurant) is narrowed by attribution,
   * not by the Orderful query.
   */
  customerId: z.string().uuid().nullable().optional(),
  /** How far back to look, in days. */
  lookbackDays: z.number().int().min(1).max(365).default(30),
});

export type ListOrderfulTransactionsInput = z.infer<
  typeof ListOrderfulTransactionsInputSchema
>;

const ListOrderfulTransactionsOutputSchema = z.object({
  transactions: z.array(OrderfulTransactionSchema),
  /** True when more of this environment's transactions match than were returned. */
  truncated: z.boolean(),
  /** Notes from the service about how the answer was bounded (shown to the user). */
  notes: z.array(z.string()),
});

export type ListOrderfulTransactionsOutput = z.infer<
  typeof ListOrderfulTransactionsOutputSchema
>;

// --- Transaction detail (print view) ---------------------------------------

const OrderfulEdiPartySchema = z.object({
  code: z.string(),
  role: z.string(),
  name: z.string().nullable(),
  identifierQualifier: z.string().nullable(),
  identifier: z.string().nullable(),
  address: z.string().nullable(),
});

const OrderfulEdiLineItemSchema = z.object({
  lineNumber: z.string().nullable(),
  quantity: z.string().nullable(),
  unitOfMeasure: z.string().nullable(),
  unitPrice: z.string().nullable(),
  description: z.string().nullable(),
  identifiers: z.array(
    z.object({
      qualifier: z.string(),
      qualifierLabel: z.string(),
      id: z.string(),
    }),
  ),
  changeCode: z.object({ code: z.string(), label: z.string() }).nullable(),
});

const OrderfulEdiDocumentSchema = z.object({
  documentCode: z.string().nullable(),
  controlNumber: z.string().nullable(),
  purchaseOrderNumber: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  shipmentIdentification: z.string().nullable(),
  documentDate: z.string().nullable(),
  parties: z.array(OrderfulEdiPartySchema),
  dates: z.array(
    z.object({
      qualifier: z.string(),
      qualifierLabel: z.string(),
      date: z.string().nullable(),
    }),
  ),
  references: z.array(
    z.object({
      qualifier: z.string(),
      qualifierLabel: z.string(),
      value: z.string().nullable(),
    }),
  ),
  lineItems: z.array(OrderfulEdiLineItemSchema),
  reportedLineCount: z.string().nullable(),
  reportedTotalAmount: z.string().nullable(),
});

export type OrderfulEdiDocumentDTO = z.infer<typeof OrderfulEdiDocumentSchema>;

const GetOrderfulTransactionInputSchema = z.object({
  transactionId: z.string().trim().min(1),
});

const GetOrderfulTransactionOutputSchema = z.object({
  id: z.string(),
  transactionType: z.string().nullable(),
  documentCode: z.string().nullable(),
  stream: z.string().nullable(),
  createdAt: z.coerce.date().nullable(),
  senderIsaId: z.string().nullable(),
  receiverIsaId: z.string().nullable(),
  matchedCustomerName: z.string().nullable(),
  validationStatus: z.string().nullable(),
  deliveryStatus: z.string().nullable(),
  acknowledgmentStatus: z.string().nullable(),
  validationErrorSummary: z.string().nullable(),
  validationErrors: z.array(
    z.object({
      dataPath: z.string().nullable(),
      message: z.string().nullable(),
      validationErrorType: z.string().nullable(),
    }),
  ),
  documents: z.array(OrderfulEdiDocumentSchema),
  /** Partner instructions (N9/MSG), purchase orders only. */
  notes: z.array(
    z.object({
      qualifier: z.string().nullable(),
      referenceId: z.string().nullable(),
      description: z.string().nullable(),
      lineNumber: z.string().nullable(),
      text: z.array(z.string()),
    }),
  ),
  /** Anything that didn't resolve while parsing, so a thin view is explained. */
  parseWarnings: z.array(z.string()),
  orderfulUrl: z.string().nullable(),
  orderfulPrintViewUrl: z.string().nullable(),
});

export type GetOrderfulTransactionOutput = z.infer<
  typeof GetOrderfulTransactionOutputSchema
>;

export const ediContracts = {
  /** Vendor implementation guidelines: the documents, and our renderings of them. */
  guidelines: ediGuidelineContracts,

  organization: {
    getIdentity: oc.output(OrganizationEdiIdentityOutputSchema).route({
      method: "GET",
      summary: "Get the organization's EDI identity",
      description:
        "Returns the organization's own EDI sender ISA ID and DUNS number, which identify it as sender and ship-from on outbound EDI documents.",
    }),
    updateIdentity: oc
      .input(UpdateOrganizationEdiIdentityInputSchema)
      .output(OrganizationEdiIdentityOutputSchema)
      .route({
        method: "POST",
        summary: "Update the organization's EDI identity",
        description:
          "Sets or clears the organization's sender ISA ID and DUNS number used on outbound EDI documents. Omitted fields stay unchanged.",
      }),

    getDefaults: oc.output(OrganizationEdiDefaultsOutputSchema).route({
      method: "GET",
      summary: "Get organization EDI defaults",
      description:
        "Returns org-wide EDI fallback settings customers inherit when they have no value of their own, such as the line description that excludes lines from documents.",
    }),
    updateDefaults: oc
      .input(UpdateOrganizationEdiDefaultsInputSchema)
      .output(OrganizationEdiDefaultsOutputSchema)
      .route({
        method: "POST",
        summary: "Update organization EDI defaults",
        description:
          "Sets or clears the org-wide default line description that drops matching lines from outbound invoices, ship notices and acknowledgments for customers without their own value.",
      }),
  },

  customers: {
    getIdentity: oc
      .input(GetCustomerEdiIdentityInputSchema)
      .output(CustomerEdiIdentityOutputSchema)
      .route({
        method: "GET",
        summary: "Get a customer's EDI identity",
        description:
          "Returns a customer's stored EDI settings: ISA ID, vendor number, DUNS, and the line description that excludes matching lines from outbound documents.",
      }),
    updateIdentity: oc
      .input(UpdateCustomerEdiIdentityInputSchema)
      .output(CustomerEdiIdentityOutputSchema)
      .route({
        method: "POST",
        summary: "Update a customer's EDI identity",
        description:
          "Sets or clears a customer's EDI ISA ID, vendor number, DUNS, or excluded line description. Omitted fields stay unchanged.",
      }),
  },

  outbound855Messages: {
    // NOTE: there is deliberately no `list` here. Every outbound 855 — whether
    // it came from an imported Famous order or was fired automatically on
    // SalesOrder approval — is listed by `orderConfirmations.list`, so the
    // Order Confirmation page is the single place to see them.
    resend: oc.input(Resend855InputSchema).output(Resend855OutputSchema).route({
      method: "POST",
      summary: "Resend an EDI 855 order acknowledgment",
      description:
        "Admin only. Manually resends the 855 purchase order acknowledgment for a sales order by order number; returns sent false if the order is closed or the customer's configuration refuses it.",
    }),
  },

  famousSalesOrders: {
    list: oc
      .input(ListFamousSalesOrdersInputSchema)
      .output(ListFamousSalesOrdersOutputSchema)
      .route({
        method: "GET",
        summary: "List ERP sales orders for EDI invoicing",
        description:
          "Paginated list of ERP sales orders imported for EDI invoicing, filterable by order or PO number, customer, invoice type (810/880), send status, fetch status, and import date.",
      }),

    refresh: oc
      .input(RefreshFamousSalesOrderInputSchema)
      .output(RefreshFamousSalesOrderOutputSchema)
      .route({
        method: "POST",
        summary: "Refresh an EDI invoice order from the ERP",
        description:
          "Re-fetches an imported sales order's details from the ERP before its EDI invoice is sent. Admins can force a refresh after sending, which clears the sent status.",
      }),
  },

  orderConfirmations: {
    list: oc
      .input(ListOrderConfirmationsInputSchema)
      .output(ListOrderConfirmationsOutputSchema)
      .route({
        method: "GET",
        summary: "List EDI 855 order acknowledgments",
        description:
          "Paginated list of outbound 855 acknowledgments, both imported ERP orders and those sent on sales order approval, filterable by order, customer, and 855 status. Refreshes pending statuses.",
      }),

    fetch: oc
      .input(FetchOrderConfirmationInputSchema)
      .output(FetchOrderConfirmationOutputSchema)
      .route({
        method: "POST",
        summary: "Import an ERP order for an 855 acknowledgment",
        description:
          "Fetches an order by its ERP order number and creates an order confirmation record for sending an 855. Fails if that order was already imported.",
      }),

    get: oc
      .input(GetOrderConfirmationInputSchema)
      .output(GetOrderConfirmationOutputSchema)
      .route({
        method: "GET",
        summary: "Get an EDI 855 order confirmation",
        description:
          "Returns one order confirmation with its ERP order details and 855 status, refreshing a pending status from the EDI network when due.",
      }),

    sync: oc
      .input(SyncOrderConfirmationInputSchema)
      .output(SyncOrderConfirmationOutputSchema)
      .route({
        method: "POST",
        summary: "Refresh an 855 order confirmation from the ERP",
        description:
          "Re-fetches the order's details from the ERP for an order confirmation. Refused after the 855 is sent unless an admin forces it, which clears the sent status.",
      }),

    delete: oc
      .input(DeleteOrderConfirmationInputSchema)
      .output(DeleteOrderConfirmationOutputSchema)
      .route({
        method: "POST",
        summary: "Delete an EDI 855 order confirmation",
        description:
          "Permanently removes an imported order confirmation record from the 855 page. Does not recall any acknowledgment already sent.",
      }),

    /**
     * The approved-sales-order half of this page. `sendEdi855` above answers
     * from an order imported from Famous; these two answer from the sales
     * order and its original 850, for customers configured that way.
     */
    resolvePurchaseOrder: oc
      .input(ResolvePurchaseOrderFor855InputSchema)
      .output(ResolvePurchaseOrderFor855OutputSchema)
      .route({
        method: "GET",
        summary: "Find sales orders by PO for an 855",
        description:
          "Looks up approved sales orders by exact customer PO number (up to 25) and reports, per order, whether an 855 acknowledgment can be sent and why not.",
      }),

    sendFromSalesOrder: oc
      .input(SendAck855FromSalesOrderInputSchema)
      .output(SendAck855FromSalesOrderOutputSchema)
      .route({
        method: "POST",
        summary: "Send an EDI 855 from a sales order",
        description:
          "Transmits an 855 purchase order acknowledgment built from an approved sales order and its original 850, after rechecking eligibility. Admins can send on the test stream instead.",
      }),

    sendEdi855: oc
      .input(SendOrderConfirmation855InputSchema)
      .output(SendOrderConfirmation855OutputSchema)
      .route({
        method: "POST",
        summary: "Send an EDI 855 for an order confirmation",
        description:
          "Transmits an 855 acknowledgment built from an imported ERP order. Admins can force a resend or send on the test stream without changing the record's status.",
      }),
  },

  orderfulConfigs: {
    listPartnerProfiles: oc.output(ListPartnerProfilesOutputSchema).route({
      method: "GET",
      summary: "List EDI partner profiles",
      description:
        "Returns the trading partner document profiles a customer's EDI configuration can select, with which document types (855, 810, 856) each can build.",
    }),

    upsert: oc
      .input(UpsertOrderfulConfigInputSchema)
      .output(UpsertOrderfulConfigOutputSchema)
      .route({
        method: "POST",
        summary: "Save a customer's EDI document configuration",
        description:
          "Creates or replaces a customer's configuration for one EDI document type (850, 855, 810, 856, 880, 875), controlling how that document is received or sent.",
      }),

    delete: oc
      .input(DeleteOrderfulConfigInputSchema)
      .output(DeleteOrderfulConfigOutputSchema)
      .route({
        method: "POST",
        summary: "Delete a customer's EDI document configuration",
        description:
          "Removes a customer's configuration for one EDI document type, which stops that document from being sent or processed for them.",
      }),

    applyPreset: oc
      .input(ApplyPartnerPresetInputSchema)
      .output(ApplyPartnerPresetOutputSchema)
      .route({
        method: "POST",
        summary: "Apply an EDI partner preset to a customer",
        description:
          "Merges a known trading partner bundle of EDI document settings into a customer's configurations, keeping customer-specific fields. Returns the document types updated.",
      }),
  },

  orderfulImport: {
    importTransaction: oc
      .input(ImportOrderfulTransactionInputSchema)
      .output(ImportOrderfulTransactionOutputSchema)
      .route({
        method: "POST",
        summary: "Import an EDI purchase order by transaction",
        description:
          "Admin only. Pulls an 850 or 875 transaction by id and stages it as a draft customer purchase order. Test mode discards earlier drafts and re-imports.",
      }),
  },

  channels: {
    list: oc.output(ListEdiChannelsOutputSchema).route({
      method: "GET",
      summary: "List EDI trading channels",
      description:
        "Returns each ISA ID the organization's customers trade on, whether it is a direct partner or an aggregator portal fronting several customers, and those customers.",
    }),

    setKind: oc
      .input(SetEdiChannelKindInputSchema)
      .output(ListEdiChannelsOutputSchema)
      .route({
        method: "POST",
        summary: "Set an EDI channel as direct or aggregator",
        description:
          "Declares whether an ISA ID identifies one direct trading partner or a portal fronting several customers told apart by DUNS. Returns the updated channel list.",
      }),
  },

  orderfulTransactions: {
    listCustomers: oc
      .output(ListOrderfulTransactionCustomersOutputSchema)
      .route({
        method: "GET",
        summary: "List customers with EDI identities",
        description:
          "Admin only. Returns every customer carrying an EDI identity, with ISA ID and vendor number, for filtering EDI transactions.",
      }),

    list: oc
      .input(ListOrderfulTransactionsInputSchema)
      .output(ListOrderfulTransactionsOutputSchema)
      .route({
        method: "GET",
        summary: "List EDI transactions",
        description:
          "Admin only. Lists recent EDI transactions of all types and directions, attributed to customers, optionally for one customer over a lookback window of up to 365 days.",
      }),

    get: oc
      .input(GetOrderfulTransactionInputSchema)
      .output(GetOrderfulTransactionOutputSchema)
      .route({
        method: "GET",
        summary: "Get an EDI transaction",
        description:
          "Admin only. Returns one EDI transaction parsed into readable documents: parties, dates, references, line items, partner notes, and validation, delivery, and acknowledgment status.",
      }),
  },
} as const;

export type EdiContracts = typeof ediContracts;
